import type { CustomerJourney, JourneyStep, JourneyNegotiation } from '@/api/customer-journey';
import { JOURNEY_STEP_ORDER } from '@/api/customer-journey';

/**
 * Shared labels / tones / small helpers for the Sale Process (journey) tab.
 * Kept framework-free so every panel derives step + status presentation from a
 * single source. Tones map to `@vnetwork/ui` Chip/Badge/StatusPill palettes.
 */

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** EN-first labels for each pipeline step (mirrors legacy CUSTOMER_JOURNEYS). */
export const STEP_LABELS: Record<string, string> = {
  searching: 'Contact & Searching',
  presentation: 'Meeting / Presentation',
  poc: 'POC / Trial',
  contract: 'Deal & Contract',
  close_win: 'Closed Won',
  close_lose: 'Closed Lost',
  terminate: 'Terminated',
};

/** Human label for a step value, falling back to a humanized slug. */
export function stepLabel(step?: string): string {
  if (!step) return '—';
  return STEP_LABELS[step] ?? step.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A journey is "lost" when it sits on the losing terminal step. */
export function isLostStep(step?: string): boolean {
  return step === 'close_lose' || step === 'terminate';
}

/** A journey is "won" when it reached the winning terminal step. */
export function isWonStep(step?: string): boolean {
  return step === 'close_win';
}

/** 0-based index of a step within the linear pipeline (-1 if terminal/unknown). */
export function stepIndex(step?: string): number {
  if (!step) return -1;
  return JOURNEY_STEP_ORDER.indexOf(step as JourneyStep);
}

/**
 * Per-step presentation tone relative to the active step:
 * done → success, current → info, future → neutral. Terminal (lost) journeys
 * paint every not-yet-done step danger so the rail reads as halted.
 */
export function railTone(step: JourneyStep, activeStep?: string): Tone {
  const active = stepIndex(activeStep);
  const idx = stepIndex(step);
  if (isLostStep(activeStep)) {
    return idx < active || active === -1 ? 'neutral' : 'danger';
  }
  if (active === -1) return 'neutral';
  if (idx < active) return 'success';
  if (idx === active) return 'info';
  return 'neutral';
}

/** Tone for a journey's overall status pill. */
export function statusTone(journey: CustomerJourney): Tone {
  if (isWonStep(journey.customer_journey_step)) return 'success';
  if (isLostStep(journey.customer_journey_step)) return 'danger';
  const s = journey.customer_journey_step_status;
  if (s === 'completed') return 'success';
  if (s === 'processing') return 'info';
  return 'neutral';
}

/** Short EN label for a journey's outcome/state, used on cards + pills. */
export function outcomeLabel(journey: CustomerJourney): string {
  if (isWonStep(journey.customer_journey_step)) return 'Won';
  if (journey.customer_journey_step === 'terminate') return 'Terminated';
  if (journey.customer_journey_step === 'close_lose') return 'Lost';
  const status = journey.customer_journey_step_status;
  return status ? status.replace(/\b\w/g, (c) => c.toUpperCase()) : 'Active';
}

/**
 * Negotiation status enum (v4 backend, verbatim): the lifecycle is
 * draft → in_progress → pending_review → approved → completed → converted,
 * with rejected / cancelled as terminal off-ramps.
 */
export const NEGOTIATION_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  in_progress: 'In Progress',
  pending_review: 'Waiting Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  completed: 'Completed',
  converted: 'Converted',
};

/** Human label for a negotiation status (falls back to a humanized slug). */
export function negotiationStatusLabel(status?: string): string {
  if (!status) return '—';
  return (
    NEGOTIATION_STATUS_LABELS[status] ??
    status.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** Negotiation status → tone. */
export function negotiationTone(status?: string): Tone {
  switch (status) {
    case 'completed':
    case 'approved':
    case 'converted':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'cancelled':
      return 'neutral';
    default:
      return 'info';
  }
}

/**
 * Whether the current user can act on a negotiation at all. Mirrors legacy
 * `canPerformActions`: it must have assignees, not be archived, and not sit in a
 * terminal cancelled/converted state.
 */
export function canActOnNegotiation(n: JourneyNegotiation): boolean {
  const status = n.contract_negotiation_status;
  return (
    (n.assignee_names?.length ?? 0) > 0 &&
    !n.archived &&
    status !== 'cancelled' &&
    status !== 'converted'
  );
}

const RESOLVABLE_STATUSES = new Set(['draft', 'in_progress', 'pending_review', 'waiting_approval']);

/** Resolve is offered on the in-flight statuses (legacy `canResolve`). */
export function canResolveNegotiation(n: JourneyNegotiation): boolean {
  return canActOnNegotiation(n) && RESOLVABLE_STATUSES.has(n.contract_negotiation_status ?? '');
}

/** Reject shares the same in-flight statuses as Resolve (legacy `canReject`). */
export function canRejectNegotiation(n: JourneyNegotiation): boolean {
  return canActOnNegotiation(n) && RESOLVABLE_STATUSES.has(n.contract_negotiation_status ?? '');
}

/** Convert-to-contract is only offered once the negotiation is completed. */
export function canConvertNegotiation(n: JourneyNegotiation): boolean {
  return canActOnNegotiation(n) && n.contract_negotiation_status === 'completed';
}

/** Sale-follower display names for a journey, de-duped by employee id. */
export function saleFollowerNames(journey: CustomerJourney): string[] {
  const seen = new Set<number>();
  const names: string[] = [];
  for (const f of journey.employee_sale_followers ?? []) {
    const id = f.employee_id;
    if (id != null && seen.has(id)) continue;
    if (id != null) seen.add(id);
    if (f.employee_name) names.push(f.employee_name);
  }
  return names;
}

/** Latest activity time for a journey (explanation update → created). */
export function journeyActivityTime(journey: CustomerJourney): number {
  const iso =
    journey.customer_journey_explaination?.explaination_updated_at ||
    journey.customer_journey_updated_at ||
    journey.customer_journey_created_at;
  const t = iso ? new Date(iso).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Sort journeys the way the legacy board does: active (unlocked) first, then by
 * most-recent activity descending.
 */
export function sortJourneys(journeys: CustomerJourney[]): CustomerJourney[] {
  return [...journeys].sort((a, b) => {
    const aLocked = a.customer_journey_locked ? 1 : 0;
    const bLocked = b.customer_journey_locked ? 1 : 0;
    if (aLocked !== bLocked) return aLocked - bLocked;
    return journeyActivityTime(b) - journeyActivityTime(a);
  });
}
